import React from 'react';
import ProfileImage from '@/components/commons/ProfileImage';
import ThumbsDownSolid from '@/components/commons/icons/ThumbsDownSolid';
import ThumbsUpSolid from '@/components/commons/icons/ThumbsUpSolid';

const circleName = ['Close', 'Medium', 'Distant', 'New'];

const sliceString = (string) => {
    if (string.length > 25) {
        return `${string.substring(0, 25)}...`;
    }
    return string.substring(0, 25);
};

const adaptableTextDynamicQuestions = (data) => {
    const unifiedContactInfo = data.unifiedContact;
    const texts = {
        primary_email: {
            title: 'Primary email?',
            subtitle: `Is this the primary email address for ${unifiedContactInfo.effectiveName}?`,
            reviewTitle: 'Clearing things up',
            reviewSubtitle: `What is ${unifiedContactInfo.effectiveName} primary email address?`,
            button1Text: 'Yes, this is the primary email',
            button2Text: 'No, this is not the primary email',
        },
        name: {
            title: 'Name?',
            subtitle: `Is this the correct name for ${unifiedContactInfo.primaryEmail}?`,
            reviewTitle: 'Name',
            reviewSubtitle: `Is ${data.contactMapping.contact.displayName} the correct name for ${unifiedContactInfo.effectiveName}?`,
            button1Text: 'Yes, this is the correct name',
            button2Text: 'No, this is not the correct name',
        },
        photo: {
            title: 'Photo?',
            subtitle: `Is this the correct photo for ${unifiedContactInfo.effectiveName}?`,
            reviewTitle: 'Photo',
            reviewSubtitle: `Is this the correct photo for ${unifiedContactInfo.effectiveName}?`,
            button1Text: 'Yes, this is the correct photo',
            button2Text: 'No, this is not the correct photo',
        },
        birthday: {
            title: 'Birthday?',
            subtitle: `Is this the correct birthday for ${unifiedContactInfo.effectiveName}?`,
            reviewTitle: 'Birthday',
            reviewSubtitle: `Is ${data.contactMapping.contact.birthday} the correct birthday date for ${unifiedContactInfo.effectiveName}?`,
            button1Text: 'Yes, this is the correct date',
            button2Text: 'No, this is not the correct date',
        },
        mapping: {
            title: 'Same person?',
            subtitle: `We think this person might be the same as ${unifiedContactInfo.effectiveName}`,
            reviewTitle: 'Linked contacts',
            reviewSubtitle: `Link ${unifiedContactInfo.effectiveName} and ${data.contactMapping.contact.effectiveName}?`,
            button1Text: 'Yes, this is the same person',
            button2Text: 'No, this is a different person',
        },
    };

    return texts[data.questionType];
};

export const circlesQuestion = (data) => {
    const unifiedContactInfo = data.unifiedContact;
    const formattedCircleName = unifiedContactInfo.proposedCircle !== null ? circleName[unifiedContactInfo.proposedCircle] : 'New';
    return {
        id: data.id,
        title: 'Hot or cold?',
        reviewTitle: 'Hot or cold',
        questionType: data.questionType,
        buttonsAlign: 'col',
        subtitle: `${unifiedContactInfo.effectiveName} is a ${formattedCircleName} Contact. Is this correct?`,
        reviewSubtitle: `${unifiedContactInfo.effectiveName} is a ${formattedCircleName} Contact`,
        contacts: [
            {
                avatar: <ProfileImage
                    brief={{
                        emailHash: unifiedContactInfo.emailHash,
                        profileImageUrl: unifiedContactInfo.photoImageUrl,
                    }}
                    height="h-[160px]"
                    width="w-[160px]"
                    externalStyles="mx-0"
                />,
                reviewAvatar: <ProfileImage
                    brief={{
                        emailHash: unifiedContactInfo.emailHash,
                        profileImageUrl: unifiedContactInfo.photoImageUrl,
                    }}
                    height="h-[40px]"
                    width="w-[40px]"
                    externalStyles="mx-0"
                />,
                givenName: unifiedContactInfo.effectiveName,
                email: unifiedContactInfo.primaryEmail,
                proposedCircle: unifiedContactInfo.proposedCircle,
                chosenCircle: unifiedContactInfo.chosenCircle,
            },
        ],
        buttons: [
            {
                label: 'Yes, this is correct',
                value: 0,
            },
            {
                label: `No, ${unifiedContactInfo.givenName || sliceString(unifiedContactInfo.primaryEmail)} is closer`,
                value: 1,
            },
            {
                label: `No, ${unifiedContactInfo.givenName || sliceString(unifiedContactInfo.primaryEmail)} is farther`,
                value: 2,
            },
        ],
    };
};

export const humanQuestion = (data) => {
    const unifiedContactInfo = data.unifiedContact;
    return {
        id: data.id,
        title: 'Human, or bot?',
        reviewTitle: 'Human, or bot?',
        questionType: data.questionType,
        buttonsAlign: 'col',
        subtitle: `${unifiedContactInfo.effectiveName} looks ${unifiedContactInfo.proposedHuman ? 'human' : 'like a bot'}. Is this correct?`,
        reviewSubtitle: `${unifiedContactInfo.effectiveName} looks ${unifiedContactInfo.proposedHuman ? 'human' : 'like a bot'}`,
        contacts: [
            {
                avatar: <ProfileImage
                    brief={{
                        emailHash: unifiedContactInfo.emailHash,
                        profileImageUrl: unifiedContactInfo.photoImageUrl,
                    }}
                    height="h-[160px]"
                    width="w-[160px]"
                    externalStyles="mx-0"
                />,
                reviewAvatar: <ProfileImage
                    brief={{
                        emailHash: unifiedContactInfo.emailHash,
                        profileImageUrl: unifiedContactInfo.photoImageUrl,
                    }}
                    height="h-[40px]"
                    width="w-[40px]"
                    externalStyles="mx-0"
                />,
                givenName: unifiedContactInfo.effectiveName,
                email: unifiedContactInfo.primaryEmail,
                proposedHuman: unifiedContactInfo.proposedHuman,
                chosenHuman: unifiedContactInfo.chosenHuman,
            },
        ],
        buttons: [
            {
                label: 'This is a human',
                value: true,
            },
            {
                label: 'This is a bot',
                value: false,
            },
        ],
    };
};

function cleanseAndFormatBday(bday) {
    if (!bday) {
        return null;
    }

    const pieces = bday.split('-');

    if (parseInt(pieces[0], 10) <= 1900) {
        return `${pieces[1]}/${pieces[2]}`;
    }

    return `${pieces[1]}/${pieces[2]}/${pieces[0]}`;
}

export const dynamicQuestion = (data) => {
    const unifiedContactInfo = data.unifiedContact;
    const formattedtexts = adaptableTextDynamicQuestions(data);

    return {
        id: data.id,
        title: formattedtexts.title,
        reviewTitle: formattedtexts.title,
        questionType: data.questionType,
        buttonsAlign: 'col',
        subtitle: formattedtexts.subtitle,
        reviewSubtitle: formattedtexts.subtitle,
        contacts: [
            {
                avatar: <ProfileImage
                    brief={{
                        emailHash: unifiedContactInfo.emailHash,
                        photoImageUrl: unifiedContactInfo.photoImageUrl,
                    }}
                    height="h-[160px]"
                    width="w-[160px]"
                    externalStyles="mx-0"
                />,
                reviewAvatar: <ProfileImage
                    brief={{
                        emailHash: unifiedContactInfo.emailHash,
                        photoImageUrl: unifiedContactInfo.photoImageUrl,
                    }}
                    height="h-[40px]"
                    width="w-[40px]"
                    externalStyles="mx-0"
                />,
                displayNameCol: true,
                givenName: unifiedContactInfo.effectiveName,
                email: 'Unified contact',
            },
        ],
        contactCard: {
            avatar: <ProfileImage
                brief={{
                    emailHash: data.contactMapping.contact.emailHash,
                    photoImageUrl: data.contactMapping.contact.photoImageUrl,
                }}
                height="h-[60px]"
                width="w-[60px]"
                externalStyles="mx-0"
            />,
            reviewAvatar: <ProfileImage
                brief={{
                    emailHash: data.contactMapping.contact.emailHash,
                    photoImageUrl: data.contactMapping.contact.photoImageUrl,
                }}
                height="h-[40px]"
                width="w-[40px]"
                externalStyles="mx-0"
            />,
            name: data.contactMapping.contact.displayName,
            email: data.contactMapping.contact.primaryEmail,
            birthday: data.questionType === 'birthday' ? cleanseAndFormatBday(data.contactMapping.contact.birthday) : null,
            emailFrom: data.contactMapping.contact.account.email,
        },
        buttons: [
            {
                key: 'contact-game-button1',
                label: formattedtexts.button1Text,
                value: true,
                iconRight: <ThumbsUpSolid className="w-4 h-4 text-white" />,
                background: 'green-400',
                fontColor: 'cirrusWhite',
                border: 'transparent',

            },
            {
                key: 'contact-game-button2',
                label: formattedtexts.button2Text,
                value: false,
                iconRight: <ThumbsDownSolid className="w-4 h-4 text-white" />,
                background: 'solidRed',
                fontColor: 'cirrusWhite',
                border: 'transparent',
            },
        ],
    };
};
